// src/InterviewsPage.js
import React, { useMemo, useState, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Avatar,
  Chip,
  IconButton,
  Stack,
  TextField,
  InputAdornment,
  Divider,
  Skeleton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { ThemeProvider, alpha } from "@mui/material/styles";
import theme from "./theme";

import NavBar from "./components/NavBar";
import FilterBar from "./components/FilterBar";
import CustomTagModal from "./components/CustomTagModal";

import { DataAndFilterContext } from "./context/DataAndFilterContext";
import { useGlobalLoading } from "./context/GlobalLoadingContext";
import BACKEND_URL from "./config";

// Qualitative satisfaction labels
import { describe } from "./utils/scoring";

/**
 * CONFIG: update these if your backend serves avatars elsewhere.
 * Put a default avatar file at: public/default-avatar.png
 */
const DEFAULT_AVATAR_URL = "/default-avatar.png";

// Keep your existing char threshold for showing More/Less
const ABOUT_ME_MAX_CHARS = 240;

// JTBD: you said 1–2 per card. Keep existing show-more functionality just in case.
const JTBD_DEFAULT_SHOW = 2;

// Custom tags preview count in header (keeps header compact)
const TAGS_PREVIEW_COUNT = 3;

function normalizeOptionalValue(val) {
  if (val === null || val === undefined) return "—";
  const s = String(val).trim();
  if (!s) return "—";
  if (s.toLowerCase() === "unknown") return "—";
  return s;
}

function isEmptyish(val) {
  const s = normalizeOptionalValue(val);
  return s === "—";
}

function originLabel(origin) {
  if (origin === "self_upload") return "Self-upload";
  if (origin === "ai_interview") return "AI interview";
  return normalizeOptionalValue(origin);
}

function getInterviewMeta(row) {
  return row?.meta || row?.interview_meta || row?.interviewMeta || {};
}

function getFocusTypeFromRow(row) {
  // Prefer top-level, then meta
  const meta = getInterviewMeta(row);
  const v =
    row?.interview_focus_type ||
    row?.focus_type ||
    meta?.interview_focus_type ||
    meta?.focus_type ||
    "product";
  return String(v || "product").toLowerCase();
}

function getTopicNameFromRow(row) {
  // Prefer top-level, then meta, then legacy product_name/name fallbacks
  const meta = getInterviewMeta(row);
  return (
    row?.interview_topic_name ||
    row?.topic_name ||
    meta?.interview_topic_name ||
    meta?.topic_name ||
    row?.product_name ||
    meta?.product_name ||
    row?.name ||
    "—"
  );
}

function getScriptIdFromRow(row) {
  const meta = getInterviewMeta(row);
  return (
    row?.interview_script_id ||
    meta?.interview_script_id ||
    row?.script_id ||
    meta?.script_id ||
    ""
  );
}

function scoreBucketKey(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return "b2"; // neutral-ish fallback
  if (n <= 2) return "b0";
  if (n <= 4) return "b1";
  if (n <= 6) return "b2";
  if (n <= 8) return "b3";
  return "b4";
}

/**
 * You MUST have a stable ID per interview to link + fetch avatar.
 * Prefer: row.interview_id
 * Fallbacks included; adjust to match your payload.
 */
function getInterviewId(row) {
  return row?.interview_id || row?.conversation_id || row?.id || null;
}

function buildInterviewHref(row) {
  const interviewId = getInterviewId(row);

  // Always URL-encode in case IDs contain special chars
  return interviewId
    ? `/interviews/${encodeURIComponent(interviewId)}/view`
    : `/interviews/unknown/view`;
}

function buildAvatarUrl(row) {
  const interviewId = getInterviewId(row);
  // Option A: serve from a Flask route like /api/interviews/<id>/avatar
  if (interviewId) return `${BACKEND_URL}/api/interviews/${interviewId}/avatar`;
  // Fallback
  return DEFAULT_AVATAR_URL;
}

function safeStopLinkNavigation(e) {
  // Use inside clickable <a> cards to prevent opening new tab
  e.preventDefault();
  e.stopPropagation();
}

function getMetaSegmentation(row) {
  // Flexible fallbacks for how meta might be shaped in your list payload.
  // Adjust/add keys if your list endpoint uses a different wrapper.
  return (
    row?.meta?.segmentation ||
    row?.interview_meta?.segmentation ||
    row?.interviewMeta?.segmentation ||
    row?.segmentation ||
    row?.meta_segmentation ||
    {}
  );
}

/**
 * Segmentation fallback requirement:
 * If Customer Story JSON segmentation fields are blank/empty/null/unknown,
 * fall back to meta.json segmentation object:
 *  - company_name -> Organization
 *  - company_size -> Org size
 *  - job_title    -> Job title
 */
function getSegFieldWithFallback(row, fieldKey) {
  const storySeg = row?.segmentation_data || {};
  const metaSeg = getMetaSegmentation(row) || {};

  if (fieldKey === "job_title") {
    const fromStory = normalizeOptionalValue(storySeg.job_title);
    if (fromStory !== "—") return fromStory;
    return normalizeOptionalValue(metaSeg.job_title);
  }

  if (fieldKey === "organization") {
    const fromStory = normalizeOptionalValue(storySeg.organization);
    if (fromStory !== "—") return fromStory;
    return normalizeOptionalValue(metaSeg.company_name);
  }

  if (fieldKey === "organization_size") {
    const fromStory = normalizeOptionalValue(storySeg.organization_size);
    if (fromStory !== "—") return fromStory;
    return normalizeOptionalValue(metaSeg.company_size);
  }

  if (fieldKey === "persona") {
    // Persona only comes from Customer Story JSON (per your note)
    return normalizeOptionalValue(storySeg.persona);
  }

  return "—";
}

function getCustomTags(row) {
  const seg = row?.segmentation_data || {};
  const tags = Array.isArray(seg?.custom_tags) ? seg.custom_tags : [];
  // Normalize: remove empties/unknowns
  return tags
    .map((t) => String(t || "").trim())
    .filter((t) => t && t.toLowerCase() !== "unknown");
}

function getSearchBlob(row) {
  const jtbd = Array.isArray(row?.jobs_to_be_done) ? row.jobs_to_be_done : [];
  const tags = getCustomTags(row);

  const parts = [
    row?.customer_name,
    row?.about_me,
    row?.origin,
    row?.conversation_date,
    // interview topic (Product vs JTBD)
    getFocusTypeFromRow(row),
    getTopicNameFromRow(row),
    getScriptIdFromRow(row),
    // segmentation (with meta fallbacks)
    getSegFieldWithFallback(row, "job_title"),
    getSegFieldWithFallback(row, "organization"),
    getSegFieldWithFallback(row, "organization_size"),
    getSegFieldWithFallback(row, "persona"),
    row?.solution_choice_category,
    row?.satisfaction_level,
    ...tags,
    ...jtbd.map((j) => j?.job_to_be_done_category),
  ];

  return parts
    .filter((p) => p !== null && p !== undefined)
    .map((p) => String(p).toLowerCase())
    .join(" ");
}

function InterviewAvatar({ row }) {
  const [errored, setErrored] = useState(false);
  const name = row?.customer_name || "";
  const initials =
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join("") || "👤";

  const src = !errored ? buildAvatarUrl(row) : DEFAULT_AVATAR_URL;

  return (
    <Avatar
      src={src}
      alt={name || "Interview avatar"}
      sx={{ width: 56, height: 56 }}
      imgProps={{
        onError: () => setErrored(true),
      }}
    >
      {initials}
    </Avatar>
  );
}

/**
 * Satisfaction dots (Option A) + qualitative label (from scoring.js describe()).
 * Works without relying solely on color: filled vs outlined dots.
 */
function DotMeter({ value, size = 8, bucketKey = "b2" }) {
  const n = Math.max(0, Math.min(10, Number(value) || 0));
  const filled = Math.round(n);

  return (
    <Box aria-hidden="true" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      {Array.from({ length: 10 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            width: size,
            height: size,
            borderRadius: "999px",
            border: "1px solid",
            bgcolor:
              i < filled
                ? (t) => (t.palette?.score?.[bucketKey]?.main || t.palette.primary.main)
                : "transparent",
            borderColor:
              i < filled
                ? (t) => (t.palette?.score?.[bucketKey]?.main || t.palette.primary.main)
                : "divider",
          }}
        />
      ))}
    </Box>
  );
}

function SatisfactionInline({
  metricKey,
  value,
  size = 8,
  emphasize = false,
  mode = "full", // "full" | "compact"
}) {
  const v = Number(value);
  const hasValue = value !== null && value !== undefined && !Number.isNaN(v);
  const qual = hasValue ? describe(metricKey, v) : "";

  const numericText = hasValue ? `${v}/10` : "—";

  const bucketKey = hasValue ? scoreBucketKey(v) : "b2";

  // Tooltip shows full meaning but the inline is still compact
  const tooltip = hasValue ? `${numericText}${qual ? ` · ${qual}` : ""}` : "No score";

  const showDots = mode === "full";

  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{ display: "inline-flex", alignItems: "center", gap: 1, cursor: "default" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {showDots ? (
          <DotMeter value={hasValue ? v : 0} size={size} bucketKey={bucketKey} />
        ) : null}

        <Typography
          variant={emphasize ? "subtitle2" : "body2"}
          sx={{
            fontWeight: emphasize ? 700 : 500,
            whiteSpace: "nowrap",
            color: (t) => (t.palette?.score?.[bucketKey]?.main || t.palette.primary.main),
          }}
        >
          {numericText}
          {qual ? (
            <Typography
              component="span"
              variant={emphasize ? "subtitle2" : "body2"}
              sx={{
                fontWeight: emphasize ? 700 : 500,
                opacity: 0.8, // keeps qual a bit softer but same hue
              }}
            >
              {" "}
              · {qual}
            </Typography>
          ) : null}
        </Typography>

      </Box>
    </Tooltip>
  );
}

function AboutMe({ text }) {
  const [expanded, setExpanded] = useState(false);
  const hasText = Boolean(text && String(text).trim());
  const full = hasText ? String(text) : "—";

  const isLong = full.length > ABOUT_ME_MAX_CHARS;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        About me
      </Typography>

      <Typography
        variant="body2"
        sx={{
          whiteSpace: expanded ? "pre-wrap" : "normal",
          display: expanded ? "block" : "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: expanded ? "unset" : 2, // Always 2 lines when collapsed
          overflow: "hidden",
        }}
      >
        {full}
      </Typography>

      {isLong && (
        <Box sx={{ mt: 0.5 }}>
          <Typography
            component="button"
            type="button"
            onClick={(e) => {
              safeStopLinkNavigation(e);
              setExpanded((v) => !v);
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              textDecoration: "underline",
              font: "inherit",
            }}
            aria-expanded={expanded}
          >
            {expanded ? "Less" : "More"}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function JtbdList({ row }) {
  const [expanded, setExpanded] = useState(false);
  const jtbd = Array.isArray(row?.jobs_to_be_done) ? row.jobs_to_be_done : [];
  const showCount = expanded ? jtbd.length : Math.min(jtbd.length, JTBD_DEFAULT_SHOW);
  const hiddenCount = Math.max(0, jtbd.length - showCount);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
        Jobs to be done
      </Typography>

      {jtbd.length === 0 ? (
        <Typography variant="body2">—</Typography>
      ) : (
        <Stack spacing={1}>
          {jtbd.slice(0, showCount).map((j, idx) => (
            <Box
              key={`${j?.job_to_be_done_category || "jtbd"}-${idx}`}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                {j?.job_to_be_done_category || "—"}
              </Typography>

              <SatisfactionInline
                metricKey="satisfaction"
                value={j?.satisfaction}
                size={6}
                emphasize={false}
                mode="compact"
              />
            </Box>
          ))}

          {jtbd.length > JTBD_DEFAULT_SHOW && (
            <Box>
              <Typography
                component="button"
                type="button"
                onClick={(e) => {
                  safeStopLinkNavigation(e);
                  setExpanded((v) => !v);
                }}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  textDecoration: "underline",
                  font: "inherit",
                }}
                aria-expanded={expanded}
              >
                {expanded ? "Show less" : `Show all (${jtbd.length})`}
              </Typography>
              {hiddenCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (+{hiddenCount} more)
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}

function SegmentationFooterRow({ row }) {
  const jobTitle = getSegFieldWithFallback(row, "job_title");
  const org = getSegFieldWithFallback(row, "organization");
  const orgSize = getSegFieldWithFallback(row, "organization_size");
  const persona = getSegFieldWithFallback(row, "persona");

  // Show only the fields you currently show, but in a single bottom row.
  // Persona is the only segmentation value that should be a chip.
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        pt: 1.25,
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Job title
          </Typography>
          <Typography variant="body2">{jobTitle}</Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            Organization
          </Typography>
          <Typography variant="body2">{org}</Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            Org size
          </Typography>
          <Typography variant="body2">{orgSize}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
          Persona
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={isEmptyish(persona) ? "Persona: —" : `Persona: ${persona}`}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </Box>
    </Box>
  );
}

function HeaderTags({ row, onEditTags }) {
  const tags = getCustomTags(row);
  const preview = tags.slice(0, TAGS_PREVIEW_COUNT);
  const remaining = Math.max(0, tags.length - preview.length);

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 0.75,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {preview.length === 0 ? (
        <Chip size="small" variant="outlined" label="No tags" />
      ) : (
        <>
          {preview.map((t) => (
            <Chip key={t} size="small" label={t} />
          ))}
          {remaining > 0 && <Chip size="small" variant="outlined" label={`+${remaining}`} />}
        </>
      )}

      <Tooltip title="Edit tags">
        <IconButton
          size="small"
          aria-label={`Edit tags for ${row?.customer_name || "interview"}`}
          onClick={(e) => {
            safeStopLinkNavigation(e);
            onEditTags(row);
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function InterviewCard({ row, availableTags, onEditTags }) {
  const href = buildInterviewHref(row);

  const solution = normalizeOptionalValue(row?.solution_choice_category);
  const date = normalizeOptionalValue(row?.conversation_date);
  const type = originLabel(row?.origin);

  const focusType = getFocusTypeFromRow(row);
  const topicName = normalizeOptionalValue(getTopicNameFromRow(row));
  const topicChipLabel =
    focusType === "jtbd" ? `Workflow: ${topicName}` : `Product: ${topicName}`;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        "&:hover": { boxShadow: 2 },
      }}
    >
      <CardActionArea
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ alignItems: "stretch" }}
      >
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <InterviewAvatar row={row} />

            {/* Left header block */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Name row + key chips */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="h6" sx={{ mr: 0.5, lineHeight: 1.2 }}>
                  {row?.customer_name || "—"}
                </Typography>

                {/* Interview type: chip with no label (per your preference) */}
                <Chip size="small" variant="outlined" label={type} />

                {/* Topic chip */}
                <Chip
                  size="small"
                  variant="outlined"
                  label={topicChipLabel}
                  title={topicChipLabel}
                  sx={{
                    maxWidth: 320,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />

                {/* Date: keep as chip */}
                <Chip size="small" variant="outlined" label={date} />

                {/* Custom tags moved next to date */}
                <HeaderTags row={row} onEditTags={onEditTags} />
              </Box>

              {/* Hint row */}
              <Box sx={{ display: "flex", alignItems: "center", mt: 0.5, gap: 0.75 }}>
                <OpenInNewIcon fontSize="small" />
                <Typography variant="caption" color="text.secondary">
                  Opens in a new tab
                </Typography>
              </Box>
            </Box>

            {/* Right header block: Solution + Satisfaction (more prominent) */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 0.75,
                minWidth: { xs: 140, sm: 220 },
              }}
            >
              {/* Solution chip WITH label (per your preference) */}
              <Box
                sx={{ textAlign: "right" }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Solution
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    lineHeight: 1.1,
                    fontWeight: 800,
                    maxWidth: 220,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={solution === "—" ? "" : solution}
                >
                  {solution}
                </Typography>
              </Box>

              {/* Overall satisfaction: dots + numeric + qualitative */}
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Satisfaction
                </Typography>
                <SatisfactionInline
                  metricKey="satisfaction_level"
                  value={row?.satisfaction_level}
                  size={7}
                  emphasize={true}
                />
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Body */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1.2fr 1fr" },
              gap: 2,
            }}
          >
            <Stack spacing={1.75}>
              <AboutMe text={row?.about_me} />
            </Stack>

            <Stack spacing={1.75}>
              <JtbdList row={row} />
            </Stack>
          </Box>

          {/* Segmentation footer row across bottom */}
          <Divider />
          <SegmentationFooterRow row={row} />
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function InterviewCardSkeleton() {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton variant="circular" width={56} height={56} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="55%" />
          </Box>
          <Box sx={{ width: 220 }}>
            <Skeleton variant="text" />
            <Skeleton variant="text" />
          </Box>
        </Box>
        <Divider />
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="rectangular" height={120} />
      </CardContent>
    </Card>
  );
}

export default function InterviewsPage() {
  const { loading } = useGlobalLoading();

  // NOTE: adjust this if your context names differ.
  const ctx = useContext(DataAndFilterContext);
  const filteredData = ctx?.filteredData || []; // FilterBar-driven filtered results
  const filterOptions = ctx?.filterOptions || {};
  const availableTags = filterOptions?.customTags || [];

  const [search, setSearch] = useState("");
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedRowForTags, setSelectedRowForTags] = useState(null);

  const openTagModalForRow = useCallback((row) => {
    setSelectedRowForTags(row);
    setTagModalOpen(true);
  }, []);

  const closeTagModal = useCallback(() => {
    setTagModalOpen(false);
    setSelectedRowForTags(null);
  }, []);

  const searchQuery = search.trim().toLowerCase();

  const searchedData = useMemo(() => {
    if (!searchQuery) return filteredData;
    return filteredData.filter((row) => getSearchBlob(row).includes(searchQuery));
  }, [filteredData, searchQuery]);

  const resultsCount = searchedData.length;

  return (
    <ThemeProvider theme={theme}>
      <NavBar />
      <Box sx={{ padding: 3 }}>
        <Typography variant="h4" gutterBottom>
          Interviews
        </Typography>
        <FilterBar />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Browse interviews, search, and open the ones that matter most.
        </Typography>

        {/* Search (only additional control) */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <TextField
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search interviews…"
            inputProps={{ "aria-label": "Search interviews" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear search"
                    onClick={() => setSearch("")}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            helperText="Search name, about me, persona, org, tags, JTBD, and solution."
          />
        </Box>

        {/* Results count */}
        {!loading && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {resultsCount} interview{resultsCount === 1 ? "" : "s"}
          </Typography>
        )}

        {/* Content */}
        {loading ? (
          <Stack spacing={2}>
            {Array.from({ length: 6 }).map((_, i) => (
              <InterviewCardSkeleton key={i} />
            ))}
          </Stack>
        ) : resultsCount === 0 ? (
          <Box sx={{ p: 3, border: "1px dashed", borderColor: "divider", borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              No interviews match your search.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Try a different query or clear your search.
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={() => setSearch("")}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                textDecoration: "underline",
                font: "inherit",
              }}
            >
              Clear search
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {searchedData.map((row, idx) => (
              <InterviewCard
                key={getInterviewId(row) || `${row?.customer_name}-${row?.conversation_date}-${idx}`}
                row={row}
                availableTags={availableTags}
                onEditTags={openTagModalForRow}
              />
            ))}
          </Stack>
        )}

        {/* Existing modal only (reused) */}
        {selectedRowForTags && (
          <CustomTagModal
            open={tagModalOpen}
            onClose={closeTagModal}
            dataFile={selectedRowForTags}
            availableTags={availableTags}
          />
        )}
      </Box>
    </ThemeProvider>
  );
}
